import { useState, useEffect, useCallback } from 'react';
import { api } from '../../api/client';
import { authAPI } from '../../services/api';
import { useTenantScope } from '../../context/TenantScopeContext.jsx';
import EmployeeRosterModal from '../../components/EmployeeRosterModal.jsx';
import {
    DEFAULT_MONTHLY_SALARY_COP,
    emptyEmployeeForm,
    employeeFormFromRecord,
    buildCreateEmployeePayload,
    buildUpdateEmployeePayload,
} from '../../utils/employeeRosterForm.js';
import { formatCOP } from '../../utils/money';
import { unwrapList } from '../../utils/apiData.js';
import { useAuth } from '../../context/AuthContext.jsx';
import { applyEmployeeListVisibility } from '../../utils/seedVisibility.js';
import './UserManagement.css';

function roleLabel(role) {
    const m = { worker: 'Colaborador', supervisor: 'Supervisor', admin: 'Admin empresa' };
    return m[role] || role;
}

export default function UserManagement() {
    const { user, platform } = useAuth();
    const [employees, setEmployees] = useState([]);
    const [areas, setAreas] = useState([]);
    const [supervisors, setSupervisors] = useState([]);
    const [reasons, setReasons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [pwdModal, setPwdModal] = useState({ show: false, employee: null, newPassword: '' });
    const [editModal, setEditModal] = useState(null);
    const [retireModal, setRetireModal] = useState(null);
    const [retireForm, setRetireForm] = useState({
        retirement_date: '',
        causal_id: '',
        position_level: 'operativo',
        custom_comments: '',
    });
    const [toast, setToast] = useState(null);

    const showToast = useCallback((msg, type) => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    }, []);

    const { tenantId, needsTenant } = useTenantScope();

    const loadAreas = useCallback(async () => {
        try {
            const areaRes = await api.getAdminAreas(tenantId).catch(() => api.getAreaSummary());
            setAreas(unwrapList(areaRes));
        } catch (err) {
            console.error(err);
            setAreas([]);
        }
    }, [tenantId]);

    const loadAll = useCallback(async () => {
        if (needsTenant) {
            setLoading(false);
            setEmployees([]);
            setAreas([]);
            return;
        }
        setLoading(true);
        try {
            let assigned = 0;
            let assignedAmount = DEFAULT_MONTHLY_SALARY_COP;
            try {
                const sync = await api.bulkAssignDefaultSalary(DEFAULT_MONTHLY_SALARY_COP);
                assigned = Number(sync?.updated) || 0;
                if (sync?.monthly_salary != null) assignedAmount = Number(sync.monthly_salary);
            } catch {
                /* La lista debe cargar aunque falle el sync masivo */
            }

            const [empSettled, areaSettled, supSettled, reasonSettled] = await Promise.allSettled([
                api.getEmployees(tenantId, { lite: true }),
                api.getAdminAreas(tenantId).catch(() => api.getAreaSummary()),
                api.getSupervisors(),
                api.getRetirementReasons(),
            ]);

            if (areaSettled.status === 'fulfilled') {
                setAreas(unwrapList(areaSettled.value));
            } else {
                setAreas([]);
                showToast('No se pudieron cargar las áreas. Revise la conexión o cree áreas en Base de personal.', 'error');
            }

            if (supSettled.status === 'fulfilled') {
                setSupervisors(unwrapList(supSettled.value));
            } else {
                setSupervisors([]);
            }

            if (reasonSettled.status === 'fulfilled') {
                setReasons(unwrapList(reasonSettled.value));
            } else {
                setReasons([]);
            }

            if (empSettled.status === 'fulfilled') {
                const list = applyEmployeeListVisibility(unwrapList(empSettled.value), {
                    seedDataVisible: platform?.seedDataVisible ?? false,
                    viewerRole: user?.rol,
                });
                setEmployees(list);
            } else {
                setEmployees([]);
                const msg =
                    empSettled.reason?.message ||
                    'No se pudo cargar el listado de colaboradores (el servidor tardó demasiado).';
                showToast(msg, 'error');
            }

            if (assigned > 0) {
                showToast(
                    `Se asignó ${formatCOP(assignedAmount)} a ${assigned} persona(s) sin sueldo en base de datos.`,
                    'success'
                );
            }
        } catch (err) {
            showToast(err.message || 'Error al cargar datos', 'error');
        } finally {
            setLoading(false);
        }
    }, [showToast, tenantId, needsTenant, platform?.seedDataVisible, user?.rol]);

    useEffect(() => {
        loadAll();
    }, [loadAll, tenantId, needsTenant]);

    const handleResetPassword = async () => {
        if (!pwdModal.newPassword.trim()) return;
        try {
            await authAPI.resetPassword({
                employeeId: pwdModal.employee.id,
                newPassword: pwdModal.newPassword,
            });
            showToast(`Contraseña de ${pwdModal.employee.full_name} actualizada`, 'success');
            setPwdModal({ show: false, employee: null, newPassword: '' });
        } catch (err) {
            showToast(err.response?.data?.error || 'Error al resetear contraseña', 'error');
        }
    };

    const openCreate = async () => {
        if (!areas.length) await loadAreas();
        setEditModal(emptyEmployeeForm());
    };

    const openEdit = async (emp) => {
        setEditModal(employeeFormFromRecord(emp));
        try {
            const res = await api.getEmployee(emp.id);
            const row = res?.data ?? res;
            if (row?.id) setEditModal(employeeFormFromRecord(row));
        } catch {
            /* Mantener datos del listado si falla el detalle */
        }
    };

    const saveEmployee = async () => {
        if (!editModal) return;

        try {
            if (editModal.mode === 'create') {
                if (!editModal.id_document?.trim() || !editModal.birth_date?.trim()) {
                    showToast('Identificación y fecha de nacimiento son obligatorias', 'error');
                    return;
                }
                if (!editModal.area_id) {
                    showToast('Selecciona un área', 'error');
                    return;
                }
                await api.createEmployee(buildCreateEmployeePayload(editModal));
                showToast('Colaborador creado', 'success');
            } else {
                await api.updateEmployee(editModal.id, buildUpdateEmployeePayload(editModal));
                showToast('Cambios guardados', 'success');
            }
            setEditModal(null);
            loadAll();
        } catch (err) {
            showToast(err.message || 'No se pudo guardar', 'error');
        }
    };

    const openRetire = (emp) => {
        const today = new Date().toISOString().slice(0, 10);
        setRetireForm({
            retirement_date: today,
            causal_id: reasons[0]?.id != null ? String(reasons[0].id) : '',
            position_level: 'operativo',
            custom_comments: '',
        });
        setRetireModal(emp);
    };

    const submitRetire = async (e) => {
        e.preventDefault();
        if (!retireModal) return;
        if (!retireForm.causal_id) {
            showToast('Selecciona una causal', 'error');
            return;
        }
        try {
            const res = await api.retireEmployee(retireModal.id, {
                retirement_date: retireForm.retirement_date,
                causal_id: Number(retireForm.causal_id),
                position_level: retireForm.position_level,
                custom_comments: retireForm.custom_comments.trim() || null,
            });
            let msg = res?.message || 'Retiro registrado correctamente';
            if (res?.costsEstimated) {
                msg += ' Sin costos de rotación para este área/nivel: CID en 0. Configúralos en la calculadora para mayor precisión.';
            }
            showToast(msg, 'success');
            setRetireModal(null);
            loadAll();
        } catch (err) {
            showToast(err.message || 'Error al procesar el retiro', 'error');
        }
    };

    const filtered = employees.filter((e) => {
        const name = String(e.full_name || '').toLowerCase();
        const code = String(e.employee_code || '');
        const doc = String(e.id_document || '');
        const q = searchTerm.toLowerCase();
        return name.includes(q) || code.toLowerCase().includes(q) || doc.includes(q);
    });

    const canChangeRole = editModal && editModal.mode === 'edit' && editModal.role === 'superadmin';

    if (needsTenant) {
        return (
            <div className="admin-page animate-in">
                <div className="glass-card" style={{ padding: '1.25rem', maxWidth: 520 }}>
                    <h2>Gestión de usuarios</h2>
                    <p className="text-muted" style={{ lineHeight: 1.5 }}>
                        Elija una empresa en <strong>«Ver por empresa»</strong> (arriba) para administrar
                        colaboradores y áreas de ese cliente.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-page animate-in">
            <header className="admin-header">
                <h1>Gestión de Usuarios</h1>
                <p className="subtitle">Sueldos en pesos colombianos (COP). Alta, edición, retiro y acceso.</p>
            </header>

            <div className="admin-controls glass-card" style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-md)', alignItems: 'center' }}>
                <input
                    type="text"
                    placeholder="Buscar por nombre, código o identificación..."
                    className="input search-input"
                    style={{ flex: '1 1 200px', minWidth: 0 }}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button type="button" className="btn btn-primary" onClick={() => void openCreate()}>
                    Nuevo colaborador
                </button>
            </div>

            <div className="admin-table-container glass-card">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Identificación</th>
                            <th>Código</th>
                            <th>Nombre</th>
                            <th>Área</th>
                            <th>Sueldo (COP)</th>
                            <th>Rol</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="7" className="text-center">
                                    Cargando...
                                </td>
                            </tr>
                        ) : filtered.length === 0 ? (
                            <tr>
                                <td colSpan="7" className="text-center text-muted">
                                    {searchTerm.trim()
                                        ? 'No hay colaboradores que coincidan con la búsqueda.'
                                        : 'No hay colaboradores activos.'}
                                </td>
                            </tr>
                        ) : (
                            filtered.map((emp) => (
                                <tr key={emp.id}>
                                    <td className="font-mono">{emp.id_document || '—'}</td>
                                    <td className="font-mono">{emp.employee_code}</td>
                                    <td className="font-bold">{emp.full_name}</td>
                                    <td>{emp.area_name}</td>
                                    <td>{formatCOP(emp.monthly_salary)}</td>
                                    <td>
                                        <span className={`badge badge-${emp.role}`}>{roleLabel(emp.role)}</span>
                                    </td>
                                    <td>
                                        <div className="user-actions-cell">
                                            <button type="button" className="user-action-btn" onClick={() => openEdit(emp)}>
                                                Editar
                                            </button>
                                            {emp.role !== 'superadmin' ? (
                                                <button type="button" className="user-action-btn user-action-btn--danger" onClick={() => openRetire(emp)}>
                                                    Retirar
                                                </button>
                                            ) : null}
                                            <button
                                                type="button"
                                                className="user-action-btn user-action-btn--accent"
                                                onClick={() => setPwdModal({ show: true, employee: emp, newPassword: '' })}
                                            >
                                                Llave
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <EmployeeRosterModal
                form={editModal}
                setForm={setEditModal}
                areas={areas}
                supervisors={supervisors}
                areasHint={
                    areas.length === 0
                        ? 'No hay áreas en esta empresa. Créelas en Base de personal → Gestionar áreas o en Implementación.'
                        : undefined
                }
                onSubmit={saveEmployee}
                onClose={() => setEditModal(null)}
                lockRole={canChangeRole}
            />

            {retireModal && (
                <div className="modal-overlay modal-overlay--align-top" role="presentation" onClick={() => setRetireModal(null)}>
                    <div
                        className="modal-content glass-card modal-wide um-modal-panel"
                        role="dialog"
                        aria-modal="true"
                        onClick={(ev) => ev.stopPropagation()}
                    >
                        <h2>Retirar colaborador</h2>
                        <p className="text-muted mt-xs">
                            <strong>{retireModal.full_name}</strong> — se desactivará el acceso y se registrará el evento para CID.
                        </p>
                        <form onSubmit={submitRetire} className="mt-lg">
                            <div className="mt-md">
                                <label className="label-sm">Fecha de baja</label>
                                <input
                                    className="input mt-xs"
                                    type="date"
                                    required
                                    value={retireForm.retirement_date}
                                    onChange={(e) => setRetireForm((f) => ({ ...f, retirement_date: e.target.value }))}
                                />
                            </div>
                            <div className="mt-md">
                                <label className="label-sm">Causal</label>
                                <select
                                    className="input mt-xs"
                                    required
                                    value={retireForm.causal_id}
                                    onChange={(e) => setRetireForm((f) => ({ ...f, causal_id: e.target.value }))}
                                >
                                    {reasons.length === 0 ? <option value="">— Sin causales —</option> : null}
                                    {reasons.map((r) => (
                                        <option key={r.id} value={r.id}>
                                            {r.nombre || r.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="mt-md">
                                <label className="label-sm">Nivel de puesto (costos)</label>
                                <select
                                    className="input mt-xs"
                                    value={retireForm.position_level}
                                    onChange={(e) => setRetireForm((f) => ({ ...f, position_level: e.target.value }))}
                                >
                                    <option value="operativo">Operativo base</option>
                                    <option value="tecnico">Técnico especializado</option>
                                    <option value="supervision">Supervisión / Liderazgo</option>
                                </select>
                            </div>
                            <div className="mt-md">
                                <label className="label-sm">Comentarios (opcional)</label>
                                <textarea
                                    className="input mt-xs"
                                    rows={3}
                                    value={retireForm.custom_comments}
                                    onChange={(e) => setRetireForm((f) => ({ ...f, custom_comments: e.target.value }))}
                                />
                            </div>
                            <div className="modal-actions mt-xl">
                                <button type="button" className="btn btn-ghost" onClick={() => setRetireModal(null)}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={!retireForm.causal_id || reasons.length === 0}>
                                    Confirmar retiro
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {pwdModal.show && (
                <div className="modal-overlay modal-overlay--align-top">
                    <div className="modal-content glass-card um-modal-panel">
                        <h2>Resetear acceso</h2>
                        <p>
                            Establece una nueva contraseña para <strong>{pwdModal.employee.full_name}</strong>
                        </p>

                        <div className="mt-lg">
                            <label className="label-sm">Nueva contraseña</label>
                            <input
                                type="text"
                                className="input mt-xs"
                                placeholder="Ej. Alinea123!"
                                value={pwdModal.newPassword}
                                onChange={(e) => setPwdModal((m) => ({ ...m, newPassword: e.target.value }))}
                            />
                        </div>

                        <div className="modal-actions mt-xl">
                            <button type="button" className="btn btn-ghost" onClick={() => setPwdModal({ show: false, employee: null, newPassword: '' })}>
                                Cancelar
                            </button>
                            <button type="button" className="btn btn-primary" onClick={handleResetPassword}>
                                Guardar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {toast && <div className={`toast ${toast.type} animate-slide-up`}>{toast.msg}</div>}
        </div>
    );
}
